import { z } from 'zod';
import { adminApiFetch } from './apiService';
import { apiSuccess, apiList, parseAdminApiResponse } from '@services/apiSchemas';

// Inner schemas — local for now; promote to @services/schemas/inventoryHold.ts
// when a 3rd service needs them. Admin uses DETAIL items for both list and show
// (controller's serialize_inventory_hold returns the full inventory shape).

// product_id is non-nullable because Inventory.belongs_to :product is required
// (app/models/inventory.rb:11); the serialize_inventory_hold helper emits
// inventory.product.id without &. — schema fails loud if BE regresses.
const InventoryDetailSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.unknown(),
}).passthrough();

const HoldItemDetailSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  inventory: InventoryDetailSchema,
}).passthrough();

// Admin's get_records and show both use serialize_inventory_hold
// (Api::Admin::V1::InventoryHoldsController line 95-123)
// Note: held_by is z.string() (non-nullable) per InventoryHold#held_by_name
// which has a "System" string fallback in app/models/inventory_hold.rb:66-68.
const AdminInventoryHoldRowSchema = z.object({
  id: z.string(),
  number: z.string(),
  reason: z.string(),
  note: z.string().nullable(),
  expires_on: z.string().nullable(),
  created_at: z.string(),
  total_quantity: z.number(),
  items: z.array(HoldItemDetailSchema),
  held_by: z.string(),
  released_by: z.string().nullable(),
}).passthrough();

// show extends row with released_at + release_note (controller line 42-45)
const AdminInventoryHoldShowSchema = AdminInventoryHoldRowSchema.extend({
  released_at: z.string().nullable(),
  release_note: z.string().nullable(),
}).passthrough();

// create returns { id, number } (controller line 71)
const InventoryHoldCreatedSchema = z.object({
  id: z.string(),
  number: z.string(),
}).passthrough();

const SuccessEnvelopeSchema = z.object({
  success: z.literal(true),
  message: z.string(),
}).passthrough();

export const inventoryHoldService = {
  getRecords: async (params: Record<string, unknown>) => {
    const res = await adminApiFetch('inventory_holds/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseAdminApiResponse(res, apiList(AdminInventoryHoldRowSchema));
  },

  getIds: async (params: Record<string, unknown>) => {
    const res = await adminApiFetch('inventory_holds/get_ids', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseAdminApiResponse(res, apiSuccess(z.array(z.string())));
  },

  get: async (id: string) => {
    const res = await adminApiFetch(`inventory_holds/${id}`);
    return parseAdminApiResponse(res, apiSuccess(AdminInventoryHoldShowSchema));
  },

  create: async (params: Record<string, unknown>) => {
    const res = await adminApiFetch('inventory_holds', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseAdminApiResponse(res, apiSuccess(InventoryHoldCreatedSchema));
  },

  batchRelease: async (ids: string[], releaseNote: string = '') => {
    const res = await adminApiFetch('inventory_holds/batch_release', {
      method: 'POST',
      body: JSON.stringify({ ids, release_note: releaseNote }),
    });
    return parseAdminApiResponse(res, SuccessEnvelopeSchema);
  },
};

export default inventoryHoldService;

export type AdminInventoryHoldRow = z.infer<typeof AdminInventoryHoldRowSchema>;
export type AdminInventoryHoldDetail = z.infer<typeof AdminInventoryHoldShowSchema>;
export type InventoryHoldCreated = z.infer<typeof InventoryHoldCreatedSchema>;
